<script setup>
    
    import { useStorage } from '@vueuse/core'
    
    const email = ref('');
    const zipcode = ref('');
    const isEmailValid = ref(true);
    const isZipcodeValid = ref(true);
    const hideEmailModal = useStorage('hideEmailModal', false);
    // Off 2026-10-01 to 10-12 while CAC ran a Klaviyo donation pop-up on OHC;
    // back on at CAC's request. Set false to pause it again. Gated here rather than in the
    // layouts because this component is mounted from five places across
    // default, archives and iframe.
    // Nothing else changes, so returning visitors who already dismissed it keep
    // that remembered through hideEmailModal.
    const EMAIL_SIGNUP_MODAL_ENABLED = true;
    const canShowModal = EMAIL_SIGNUP_MODAL_ENABLED && !hideEmailModal.value;

    const emit = defineEmits(['closeEmailModal']);

    function dismissModal() {
        hideEmailModal.value = true;
        nextTick(() => {
            emit('closeEmailModal', 'dismiss');
        });
    }

    // Clicking the backdrop dismisses, same as the X and the Dismiss button —
    // including remembering it, since a backdrop click is a deliberate "go
    // away" and a modal that returned on the next page would be worse.
    //
    // Tracked across mousedown and click rather than a plain @click.self: a
    // `click` fires on the nearest common ancestor of its mousedown and mouseup
    // targets, so drag-selecting inside one of the inputs and releasing over
    // the backdrop targets the section and would close the modal, throwing away
    // whatever had been typed. Only a press that both starts and ends on the
    // backdrop counts.
    const pressStartedOnBackdrop = ref(false);

    function handleBackdropMousedown(event) {
        pressStartedOnBackdrop.value = event.target === event.currentTarget;
    }

    function handleBackdropClick(event) {
        if (event.target !== event.currentTarget) return;
        if (!pressStartedOnBackdrop.value) return;
        pressStartedOnBackdrop.value = false;
        dismissModal();
    }

    function handleEmailBlur(e) {
        const checkEmail = validateEmail(e.target.value);
        if (checkEmail) isEmailValid.value = true;
        else isEmailValid.value = false;
    }

    function handleZipcodeBlur(e) {
        const checkZip = validateZipcode(e.target.value);
        if (checkZip) isZipcodeValid.value = true;
        else isZipcodeValid.value = false;
    }

    function validateEmail(value) {
        let regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

        if (regex.test(value) || value == '') return true;
        else return false;
    }

    function validateZipcode(value) {
        // Regex to check valid US and Canadian zip codes
        // Matches: 90210, 90210-1234, 902101234, H0H0H0, h0h0h0, H0H 0H0, h0h 0h0
        // Non-Matches: 9021090210, California, ABC123, ABC 123, H0H H0H
        let regex = new RegExp(/^((\d{5}-?\d{4})|(\d{5})|([A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d))$/);

        if (regex.test(value) || value == '') return true;
        else return false;
    }

    async function submitModal() {
        const isEmailValid = validateEmail(email.value);
        const isZipcodeValid = validateZipcode(zipcode.value);

        if (!isEmailValid || !isZipcodeValid) return;

        const data = {
            fields: {
                Source: 'Home Modal',
                'E-Mail Address': email.value,
                'Zip Code': zipcode.value,
            },
        };

        await $fetch('/api/signup', {
            method: 'POST',
            body: JSON.stringify(data),
        })
            .then(() => {
                console.log('Successful addEmailSignup response: ', response);
            })
            .catch((error) => {
                console.error('addEmailSignup error: ', error);
            });

        emit('closeEmailModal', 'submit');
    }
</script>

<template>
    <section
        class="email-modal"
        v-if="canShowModal"
        @mousedown="handleBackdropMousedown"
        @click="handleBackdropClick">
        <div class="card-body">
            <button type="button" @click.prevent="dismissModal" class="close-button" aria-label="Close">
                <SvgsClose />
            </button>

            <div class="content">
                <h2 class="subhead-2">Enter your email for updates from CAC</h2>

                <div class="form-wrapper">
                    <fieldset class="form-field">
                        <label for="email">
                            <h3 class="subhead-small">Email</h3>
                        </label>
                        <input v-model="email" @blur="handleEmailBlur($event)" type="text" id="email" />
                        <p v-if="!isEmailValid" class="tiny-book error">This email is invalid.</p>
                    </fieldset>
                    <fieldset class="form-field">
                        <label for="zipcode">
                            <h3 class="subhead-small">Zipcode</h3>
                        </label>
                        <input v-model="zipcode" @blur="handleZipcodeBlur($event)" type="text" id="zipcode" />
                        <p v-if="!isZipcodeValid" class="tiny-book error">This zipcode is invalid.</p>
                    </fieldset>
                </div>

                <div class="buttons-wrapper">
                    <button @click.prevent="submitModal" class="submit small-heavy uppercase">Submit</button>
                    <button @click.prevent="dismissModal" class="dismiss small-heavy uppercase">Dismiss</button>
                </div>
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
    .email-modal {
        z-index: 400;
        position: fixed;
        background: rgba(0, 0, 0, 0.5);
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 15px;

        .card-body {
            position: relative;
            padding: 50px 30px;
            background-color: $white;
            text-align: center;

            .close-button {
                position: absolute;
                // The button was the bare 14x14 glyph with no padding, so the
                // hit area was the mark itself — under the 24x24 WCAG 2.5.8
                // minimum, and people missed it and clicked again: ~139
                // rageclicks from ~139 distinct people, almost exactly one
                // apiece, which is the signature of a miss rather than a
                // broken control.
                //
                // 15px of padding around a 14px glyph gives a 44x44 target.
                // top/right drop from 15 to 0 to compensate, so the mark still
                // renders in exactly the same place: 15 + 7 = 22px from each
                // edge, as before.
                top: 0;
                right: 0;
                padding: 15px;
            }

            h2 {
                margin-bottom: 10px;
            }

            p {
                margin-bottom: 25px;
            }

            .error {
                color: red;
                margin-bottom: 0 !important;
            }

            .form-wrapper {
                margin-bottom: 40px;
                .form-field {
                    padding-top: 15px;
                    .subhead-small {
                        margin-bottom: 0px;
                    }
                    label {
                        display: flex;
                        align-items: flex-end;
                        margin-bottom: 6px;
                    }

                    input {
                        margin-bottom: 5px;
                    }
                }
            }

            .buttons-wrapper {
                display: flex;
                flex-direction: column;

                button {
                    width: 100%;
                    max-width: 145px;
                    margin-right: auto;
                    margin-left: auto;
                    padding: 10px;
                    font-family: $title;
                    background-color: $gray-light;
                    color: $black;
                    &.submit {
                        margin-bottom: 15px;
                        background-color: $black;
                        color: $white;
                    }
                }
            }
        }
    }
</style>
